import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import OrderApprovalIndex from 'src/containers/PnD/Security/OrderApproval';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { OPERATIONAL_STATUS_SECURITY_ORDER_LIST } from 'src/utils/pnd/PndConstants';
const DashboardCard = ({ icon, title, count, color, gradient }) => (
    <Card className="dashboard-card shadow-sm border-0 h-100" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <CardBody className="p-3 position-relative" style={{ background: gradient }}>
            <div className="d-flex align-items-center justify-content-between text-white">
                <div>
                    <p className="mb-0 text-white-50 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.8px' }}>{title}</p>
                    <h3 className="mb-0 fw-bold mt-1">{count}</h3>
                </div>
                <div className="bg-white bg-opacity-25 p-2 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                    {React.cloneElement(icon, { style: { fontSize: '1.5rem' } })}
                </div>
            </div>
            {/* Decorative circle */}
            <div className="position-absolute bg-white opacity-10 rounded-circle" style={{ width: '100px', height: '100px', top: '-30px', right: '-30px' }}></div>
        </CardBody>
    </Card>
);

const SecurityApproval = () => {
    const { t } = useTranslation('common');
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, declined: 0 });
    const [approvalRate, setApprovalRate] = useState(0);

    const [todayCategories, setTodayCategories] = useState(['8 AM', '10 AM', '12 PM', '2 PM', '4 PM', '6 PM']);
    const [todaySeries, setTodaySeries] = useState([{ name: 'Orders', data: [0, 0, 0, 0, 0, 0] }]);

    const [weekCategories, setWeekCategories] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
    const [weekSeries, setWeekSeries] = useState([{ name: 'Orders', data: [0, 0, 0, 0, 0, 0, 0] }]);

    useEffect(() => {
        PndRestApi.GetSecurityDashboardStats().then(response => {
            if (response && response.data && response.data.status === 200) {
                const dashboardData = response.data.data || {};
                const summary = dashboardData.summary || {};
                
                setStats({
                    total: summary.totalOrders ?? 0,
                    pending: summary.pendingApproval ?? 0,
                    approved: summary.approved ?? 0,
                    declined: summary.declined ?? 0
                });
                setApprovalRate(summary.approvalRate ?? 0);

                if (dashboardData.todayActivity) {
                    setTodayCategories(dashboardData.todayActivity.categories || []);
                    setTodaySeries(dashboardData.todayActivity.series || []);
                }
                if (dashboardData.weekActivity) {
                    setWeekCategories(dashboardData.weekActivity.categories || []);
                    setWeekSeries(dashboardData.weekActivity.series || []);
                }
            } else {
                loadFallbackStats();
            }
        }).catch(err => {
            console.warn('Dashboard stats dynamic fetch error (using fallback):', err);
            loadFallbackStats();
        });
    }, []);

    const loadFallbackStats = () => {
        PndRestApi.GetSecurityOrderList({ operationalStatus: OPERATIONAL_STATUS_SECURITY_ORDER_LIST, page: 0, size: 100 }).then(response => {
            if (response && response.data && response.data.status === 200) {
                const paged = response.data.data || {};
                const orderData = paged.content || [];
                const total = orderData.length;
                const pending = orderData.filter(r => r.securityApprovalStatus === null).length;
                const approved = orderData.filter(r => r.securityApprovalStatus === 1).length;
                const declined = orderData.filter(r => r.securityApprovalStatus === 2).length;
                
                setStats({ total, pending, approved, declined });
                const calculatedRate = total > 0 ? ((approved / total) * 100).toFixed(1) : 0;
                setApprovalRate(calculatedRate);
                
                // Default fallback mock values
                setTodayCategories(['8 AM', '10 AM', '12 PM', '2 PM', '4 PM', '6 PM']);
                setTodaySeries([{ name: 'Orders', data: [5, 12, 25, 18, 30, 22] }]);
                setWeekCategories(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
                setWeekSeries([{ name: 'Orders', data: [45, 52, 38, 65, 48, 55, 60] }]);
            }
        }).catch(err => console.error('Dashboard fallback stats fetch error:', err));
    };

    // const chartOptions = useMemo(() => ({
    //     chart: {
    //         type: 'radialBar',
    //         offsetY: 0,
    //     },
    //     plotOptions: {
    //         radialBar: {
    //             startAngle: -90,
    //             endAngle: 90,
    //             hollow: {
    //                 margin: 15,
    //                 size: '60%'
    //             },
    //             track: {
    //                 background: "#e7e7e7",
    //                 strokeWidth: '97%',
    //                 margin: 5,
    //                 dropShadow: {
    //                     enabled: true,
    //                     top: 2,
    //                     left: 0,
    //                     color: '#999',
    //                     opacity: 1,
    //                     blur: 2
    //                 }
    //             },
    //             dataLabels: {
    //                 name: {
    //                     show: true,
    //                     offsetY: 20,
    //                     color: '#888',
    //                     fontSize: '17px'
    //                 },
    //                 value: {
    //                     offsetY: -10,
    //                     fontSize: '32px',
    //                     fontWeight: 'bold',
    //                     color: '#111',
    //                     formatter: function (val) {
    //                         return val + "%";
    //                     }
    //                 }
    //             }
    //         }
    //     },
    //     grid: {
    //         padding: {
    //             top: -10,
    //             bottom: 20
    //         }
    //     },
    //     fill: {
    //         type: 'gradient',
    //         gradient: {
    //             shade: 'light',
    //             shadeIntensity: 0.4,
    //             inverseColors: false,
    //             opacityFrom: 1,
    //             opacityTo: 1,
    //             stops: [0, 50, 53, 91]
    //         },
    //     },
    //     labels: ['Approval Performance'],
    //     colors: ['#20E647'],
    // }), []);

    // const chartSeries = useMemo(() => [parseFloat(approvalRate)], [approvalRate]);

    const todayChartOptions = useMemo(() => ({
        chart: {
            id: 'today-orders',
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.9,
                stops: [0, 90, 100]
            }
        },
        xaxis: {
            categories: todayCategories,
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false },
        colors: ['#667eea'],
        tooltip: { theme: 'dark' }
    }), [todayCategories]);

    const todayChartSeries = todaySeries;

    const weekChartOptions = useMemo(() => ({
        chart: {
            id: 'week-orders',
            toolbar: { show: false }
        },
        plotOptions: {
            bar: {
                borderRadius: 5,
                columnWidth: '50%',
                endingShape: 'rounded'
            }
        },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        xaxis: {
            categories: weekCategories,
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false },
        colors: ['#f5576c'],
        tooltip: { theme: 'dark' }
    }), [weekCategories]);

    const weekChartSeries = weekSeries;

    return (
        <div className="security-dashboard">
            <h5 className="mb-3 fw-bold text-dark">Dashboard Overview</h5>
            <div>
                <Row className="mb-4 g-2 justify-content-center">
                    <Col md={2} sm={3} xs={6}>
                        <div className="p-2 rounded-3 shadow-sm position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', minHeight: '80px' }}>
                            <div className="position-relative" style={{ zIndex: 1 }}>
                                <h6 className="text-uppercase mb-0 opacity-75" style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px', marginTop: 'auto' }}>Total Orders</h6>
                            </div>
                            <h3 className="mb-0 fw-bold position-absolute" style={{ top: '10px', right: '15px', zIndex: 2, fontSize: '1.5rem' }}>{stats.total}</h3>
                            <div style={{ position: 'absolute', right: '-15px', top: '-15px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }}></div>
                        </div>
                    </Col>
                    <Col md={2} sm={3} xs={6}>
                        <div className="p-2 rounded-3 shadow-sm position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #a6a925 0%, #d0b60d 100%)', color: 'white', minHeight: '80px' }}>
                            <div className="position-relative" style={{ zIndex: 1 }}>
                                <h6 className="text-uppercase mb-0 opacity-75" style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px' }}>Pending Approval</h6>
                            </div>
                            <h3 className="mb-0 fw-bold position-absolute" style={{ top: '10px', right: '15px', zIndex: 2, fontSize: '1.5rem' }}>{stats.pending}</h3>
                            <div style={{ position: 'absolute', right: '-15px', top: '-15px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }}></div>
                        </div>
                    </Col>
                    <Col md={2} sm={3} xs={6}>
                        <div className="p-2 rounded-3 shadow-sm position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #12502b 0%, #006f36 100%)', color: 'white', minHeight: '80px' }}>
                            <div className="position-relative" style={{ zIndex: 1 }}>
                                <h6 className="text-uppercase mb-0 opacity-75" style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px' }}>Approved</h6>
                            </div>
                            <h3 className="mb-0 fw-bold position-absolute" style={{ top: '10px', right: '15px', zIndex: 2, fontSize: '1.5rem' }}>{stats.approved}</h3>
                            <div style={{ position: 'absolute', right: '-15px', top: '-15px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }}></div>
                        </div>
                    </Col>
                    <Col md={2} sm={3} xs={6}>
                        <div className="p-2 rounded-3 shadow-sm position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)', color: 'white', minHeight: '80px' }}>
                            <div className="position-relative" style={{ zIndex: 1 }}>
                                <h6 className="text-uppercase mb-0 opacity-75" style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px' }}>Declined</h6>
                            </div>
                            <h3 className="mb-0 fw-bold position-absolute" style={{ top: '10px', right: '15px', zIndex: 2, fontSize: '1.5rem' }}>{stats.declined}</h3>
                            <div style={{ position: 'absolute', right: '-15px', top: '-15px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }}></div>
                        </div>
                    </Col>
                </Row>
            </div>
            <div>
                <OrderApprovalIndex />
            </div>
            <Row className="g-3 mb-2">
               
                <Col md={12} lg={4}>
                    <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                        <CardBody className="p-3">
                            <h6 className="fw-bold mb-3">Today's Activity</h6>
                            <div style={{ height: '200px' }}>
                                <Chart options={todayChartOptions} series={todayChartSeries} type="area" height={200} />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={12} lg={4}>
                    <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                        <CardBody className="p-3">
                            <h6 className="fw-bold mb-3">This Week's Activity</h6>
                            <div style={{ height: '200px' }}>
                                <Chart options={weekChartOptions} series={weekChartSeries} type="bar" height={200} />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>


        </div>
    );
};

export default SecurityApproval;
